// =============================================================================
// Night Operations Panel — Full control panel (presets + sliders + toggles)
// Used as a slide-in drawer from the TopBar or Dashboard settings.
// =============================================================================

import { X, Flashlight, Eye, ZoomIn, Sun, Contrast, Wind, Sparkles, Info, EyeOff } from 'lucide-react';
import {
  useNightOpsStore,
  NIGHT_PRESETS,
  type NightPreset,
  type TextScale,
} from '../../stores/nightOps.store.js';

// =============================================================================
// FocusBeamExitChip — Always-visible floating button to exit Focus Beam mode.
// Fixed position, highest z-index, never dimmed, vivid amber so it cuts
// through the darkened overlay regardless of what is behind it.
// =============================================================================
export function FocusBeamExitChip(): JSX.Element | null {
  const { focusBeam, active, toggleFocusBeam } = useNightOpsStore();
  if (!active || !focusBeam) return null;

  return (
    <button
      data-testid="focus-beam-exit-chip"
      data-night-exempt
      onClick={toggleFocusBeam}
      title="Exit Focus Beam — restore normal panel visibility"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 20px',
        borderRadius: 40,
        cursor: 'pointer',
        background: 'rgba(10,12,20,0.97)',
        border: '2px solid rgba(251,191,36,0.8)',
        color: '#FCD34D',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.1em',
        backdropFilter: 'blur(12px)',
        // Vivid pulsing glow so it's impossible to miss
        boxShadow:
          '0 0 16px rgba(251,191,36,0.6), 0 0 40px rgba(251,191,36,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
        animation: 'focus-chip-pulse 2s ease-in-out infinite',
        userSelect: 'none',
      }}
    >
      <EyeOff
        style={{ width: 15, height: 15, filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.9))' }}
        strokeWidth={2.2}
      />
      EXIT FOCUS BEAM
    </button>
  );
}

interface NightOpsPanelProps {
  onClose: () => void;
}

const TEXT_SCALE_OPTIONS: { value: TextScale; label: string }[] = [
  { value: 'small',  label: 'Small'  },
  { value: 'medium', label: 'Medium' },
  { value: 'large',  label: 'Large'  },
  { value: 'xl',     label: 'X-Large' },
];

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#475569', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 6, letterSpacing: '0.04em' }}>{label}</div>
        {children}
      </div>
    </div>
  );
}

function Slider({ value, min, max, onChange, suffix = '%', color = '#25B5A8' }: {
  value: number; min: number; max: number; onChange: (v: number) => void; suffix?: string; color?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: color, height: 4 }} />
      <span style={{ fontSize: 11, color: '#94A3B8', minWidth: 36, textAlign: 'right', fontFamily: 'monospace' }}>
        {value}{suffix}
      </span>
    </div>
  );
}

export function NightOpsPanel({ onClose }: NightOpsPanelProps) {
  const store = useNightOpsStore();

  return (
    <div
      data-testid="night-ops-panel"
      style={{
        position: 'fixed',
        top: 56, right: 12, bottom: 12,
        width: 320,
        zIndex: 9000,
        display: 'flex', flexDirection: 'column',
        borderRadius: 14,
        background: 'rgba(8,14,26,0.97)',
        border: '1px solid rgba(251,191,36,0.25)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(251,191,36,0.08)',
        backdropFilter: 'blur(20px)',
        animation: 'night-enter 0.25s ease',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
        borderBottom: '1px solid rgba(40,60,90,0.5)', flexShrink: 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7,
          background: store.active ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
          border: store.active ? '1px solid rgba(251,191,36,0.35)' : '1px solid rgba(40,68,116,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Flashlight className="h-3.5 w-3.5" style={{ color: store.active ? '#FBbf24' : '#475569' }} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>Night Operations Mode</div>
          <div style={{ fontSize: 10, color: store.active ? 'rgba(251,191,36,0.6)' : '#475569' }}>
            {store.active ? 'ACTIVE' : 'STANDBY'}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 4 }}>
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Master toggle */}
        <div style={{ marginBottom: 20 }}>
          <button
            data-testid="night-ops-master-toggle"
            onClick={store.toggle}
            style={{
              width: '100%', padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
              background: store.active
                ? 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.15))'
                : 'rgba(255,255,255,0.04)',
              border: store.active ? '1px solid rgba(251,191,36,0.5)' : '1px solid rgba(40,68,116,0.5)',
              color: store.active ? '#FCD34D' : '#94A3B8',
              boxShadow: store.active ? '0 0 20px rgba(251,191,36,0.15)' : 'none',
              transition: 'all 0.3s',
            }}
          >
            <Flashlight className="h-4 w-4" strokeWidth={1.75}
              style={{ filter: store.active ? 'drop-shadow(0 0 6px rgba(251,191,36,0.9))' : 'none' }} />
            {store.active ? 'Deactivate Night Mode' : 'Activate Night Mode'}
          </button>
        </div>

        {/* Presets */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#475569', marginBottom: 10 }}>
            Engineering Presets
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(Object.entries(NIGHT_PRESETS) as [NightPreset, typeof NIGHT_PRESETS[NightPreset]][]).map(([id, p]) => (
              <button
                key={id}
                data-testid={`night-preset-${id}`}
                onClick={() => store.setPreset(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  background: store.preset === id ? 'rgba(251,191,36,0.07)' : 'rgba(255,255,255,0.025)',
                  border: store.preset === id ? '1px solid rgba(251,191,36,0.3)' : '1px solid rgba(40,68,116,0.3)',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>{p.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: store.preset === id ? '#FCD34D' : '#CBD5E1' }}>
                    {p.label}
                  </div>
                  <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{p.description}</div>
                </div>
                {store.preset === id && (
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#FBbf24', flexShrink: 0 }} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Fine-tune controls */}
        <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#475569', marginBottom: 10 }}>
          Fine-Tune Controls
        </div>

        <Row icon={<Sun className="h-4 w-4" />} label={`Brightness — ${store.brightness}%`}>
          <Slider value={store.brightness} min={20} max={100} onChange={store.setBrightness} />
        </Row>

        <Row icon={<Contrast className="h-4 w-4" />} label={`Contrast — ${store.contrast}%`}>
          <Slider value={store.contrast} min={60} max={130} onChange={store.setContrast} color="#a78bfa" />
        </Row>

        <Row icon={<ZoomIn className="h-4 w-4" />} label="Reading Text Size">
          <div style={{ display: 'flex', gap: 6 }}>
            {TEXT_SCALE_OPTIONS.map((opt) => (
              <button key={opt.value}
                data-testid={`text-scale-${opt.value}`}
                onClick={() => store.setTextScale(opt.value)}
                style={{
                  flex: 1, padding: '5px 0', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                  background: store.textScale === opt.value ? 'rgba(37,181,168,0.12)' : 'rgba(255,255,255,0.03)',
                  border: store.textScale === opt.value ? '1px solid rgba(37,181,168,0.35)' : '1px solid rgba(40,68,116,0.3)',
                  color: store.textScale === opt.value ? '#5eead4' : '#64748B',
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Row>

        {/* Toggles */}
        <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#475569', marginBottom: 10, marginTop: 8 }}>
          Accessibility Options
        </div>

        {[
          { key: 'focusBeam',    label: 'Focus Beam',   sub: 'Dims inactive panels, highlights current',  icon: Eye,      val: store.focusBeam,    fn: store.toggleFocusBeam },
          { key: 'reduceGlow',   label: 'Reduce Glow',  sub: 'Removes neon effects and box shadows',      icon: Sparkles, val: store.reduceGlow,   fn: () => store.setReduceGlow(!store.reduceGlow) },
          { key: 'reduceMotion', label: 'Reduce Motion', sub: 'Stops all animations (accessibility)',    icon: Wind,     val: store.reduceMotion, fn: () => store.setReduceMotion(!store.reduceMotion) },
        ].map(({ key, label, sub, icon: Icon, val, fn }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
            padding: '8px 10px', borderRadius: 8,
            background: val ? 'rgba(37,181,168,0.05)' : 'rgba(255,255,255,0.02)',
            border: val ? '1px solid rgba(37,181,168,0.2)' : '1px solid rgba(40,68,116,0.25)' }}>
            <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: val ? '#25B5A8' : '#475569' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1' }}>{label}</div>
              <div style={{ fontSize: 10, color: '#475569' }}>{sub}</div>
            </div>
            <button
              data-testid={`night-toggle-${key}`}
              onClick={fn}
              style={{
                width: 36, height: 20, borderRadius: 10, cursor: 'pointer', position: 'relative',
                background: val ? '#25B5A8' : 'rgba(40,68,116,0.5)',
                border: 'none', transition: 'all 0.2s', flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 2, width: 16, height: 16,
                borderRadius: '50%', background: 'white',
                transition: 'left 0.2s',
                left: val ? 18 : 2,
              }} />
            </button>
          </div>
        ))}

        {/* Voice command info */}
        <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8,
          background: 'rgba(37,181,168,0.04)', border: '1px solid rgba(37,181,168,0.15)' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Info className="h-3.5 w-3.5 shrink-0" style={{ color: '#25B5A8', marginTop: 0.5 }} />
            <span style={{ fontSize: 10, color: '#5eead4', fontWeight: 600 }}>Voice Commands</span>
          </div>
          <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.8, fontFamily: 'monospace' }}>
            "R2, activate night mode"<br />
            "R2, flashlight mode"<br />
            "R2, I'm working in the dark"<br />
            "R2, cinema mode"<br />
            "R2, blueprint mode"
          </div>
        </div>
      </div>
    </div>
  );
}
